import { useEffect, useMemo, useRef, useState } from 'react';
import { useSharedStore } from '../../hooks/useSharedStore';
import PropTypes from 'prop-types';
import { Plus, Search, Pen, Trash2, X as IconX, Image as ImageIcon, Upload, FolderTree, Layers, ArrowLeft, ChevronLeft, ChevronRight } from 'lucide-react';
import { motion } from 'framer-motion';
import { CustomSelect } from '../../components/CustomSelect';
import { formatCategoryName } from '../../utils/formatters';
import { compressImage } from '../../utils/image';
import { IconAction, RowActions } from '../../components/RowActions';
import { ViewToggle } from '../../components/ViewToggle';
import { StatusToggle } from '../../components/StatusToggle';

const blank = (orgId, needsParent) => ({
  id: null,
  orgId,
  name: '',
  description: '',
  image: null,
  ...(needsParent ? { categoryId: '' } : {}),
  gstId: '',
  taxMode: 'exclusive',
});

const Form = ({ open, draft, setDraft, onClose, onSubmit, mode, title, needsParent, parents, parentLabel, gstRates = [], accent, showNotification }) => {
  const fileRef = useRef(null);
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (!open) setErrors({});
  }, [open]);

  if (!open) return null;

  const handleSubmit = () => {
    const newErrors = {};
    if (!draft.name || !draft.name.trim()) newErrors.name = 'Name is required.';
    if (needsParent && !draft.categoryId) newErrors.categoryId = `Please select a ${parentLabel}.`;

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      if (showNotification) showNotification('Please fix the highlighted fields.', 'error');
      return;
    }
    setErrors({});
    onSubmit(draft);
  };

  // Stored straight from the file picker, a category picture went into the
  // database at whatever size the camera produced. Two categories and two
  // sub-categories came to 46 MB between them, and both catalogue screens read
  // them on open — which is why those two screens, and only those two, stalled
  // part-way through their transition while the browser worked through it.
  const onPickImage = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const image = await compressImage(file);
    setDraft((prev) => ({ ...prev, image }));
  };

  return (
    <motion.section
      initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}
      className="w-full h-full flex flex-col flex-1 bg-white rounded-lg shadow-sm border border-[var(--theme-tertiary)] overflow-hidden"
    >
      <header className="px-6 py-4 border-b border-[var(--theme-tertiary)] flex items-center justify-between shrink-0 bg-slate-50">
        <div className="flex items-center gap-3">
          <button type="button" onClick={onClose} aria-label="Go back" className="w-8 h-8 rounded-lg border border-slate-200 bg-white flex items-center justify-center text-slate-400 hover:text-slate-700 hover:bg-slate-50 transition-colors shadow-sm">
            <ArrowLeft size={16} />
          </button>
          <div>
            <h3 className="text-lg font-extrabold text-[#0d0d0d]">{mode === 'edit' ? `Edit ${title}` : `New ${title}`}</h3>
            <p className="text-xs font-semibold uppercase tracking-[0.16em] text-slate-400 mt-1">Master catalog</p>
          </div>
        </div>
      </header>

      <div className="px-6 py-5 overflow-y-auto custom-scrollbar flex flex-col gap-4 flex-1">
        <div className="flex items-center gap-4">
          <div className="w-20 h-20 rounded-lg overflow-hidden border border-[var(--theme-tertiary)] flex items-center justify-center shrink-0" style={{ background: 'var(--theme-secondary)' }}>
            {draft.image
              ? <img src={draft.image} alt="Preview" className="w-full h-full object-cover" />
              : <ImageIcon size={26} className="text-slate-300" aria-hidden="true" />}
          </div>
          <div className="flex flex-col gap-2">
            <button type="button" onClick={() => fileRef.current?.click()} className="inline-flex items-center gap-2 px-4 py-2 rounded-lg border border-[var(--theme-tertiary)] hover:border-[var(--theme-primary-tint)] hover:text-[var(--theme-primary)] text-slate-700 text-xs font-semibold uppercase tracking-[0.14em]">
              <Upload size={12} aria-hidden="true" /> Upload image
            </button>
            {draft.image && (
              <button type="button" onClick={() => setDraft({ ...draft, image: null })} className="text-xs font-semibold uppercase tracking-[0.14em] text-rose-500 text-left">Remove image</button>
            )}
            <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={onPickImage} />
          </div>
        </div>

        {needsParent && (
          <label className="block space-y-1">
            <span className="text-[11px] font-semibold uppercase tracking-[0.14em] text-slate-500">
              {parentLabel} {errors.categoryId && <span className="text-rose-500 ml-1">*</span>}
            </span>
            <CustomSelect
              value={draft.categoryId || ''}
              onChange={(v) => {
                setDraft({ ...draft, categoryId: v });
                if (errors.categoryId) setErrors({ ...errors, categoryId: undefined });
              }}
              placeholder={`— Select ${parentLabel} —`}
              options={parents.map((p) => ({ value: p.id, label: p.name }))}
              className={`w-full rounded-lg bg-gray-50 border ${errors.categoryId ? 'border-rose-400' : 'border-gray-100'} normal-case text-[#0d0d0d]`}
            />
            {errors.categoryId && <p className="text-[11px] font-medium text-rose-500">{errors.categoryId}</p>}
          </label>
        )}

        {/* The rate everything on this shelf is taxed at, and whether prices include it. */}
        {needsParent && (
          <div className="grid gap-3 sm:grid-cols-2">
            <label className="block space-y-1">
              <span className="text-[11px] font-semibold uppercase tracking-[0.14em] text-slate-500">
                GST Rate
              </span>
              <CustomSelect
                value={draft.gstId || ''}
                onChange={(v) => setDraft({ ...draft, gstId: v })}
                placeholder={gstRates.length > 0 ? "— Optional —" : "— No GST rates defined —"}
                options={gstRates.map((g) => ({ value: g.id, label: `${g.name} · ${g.rate}%` }))}
                className="w-full rounded-lg bg-gray-50 border border-gray-100 normal-case text-[#0d0d0d]"
              />
            </label>

            <label className="block space-y-1">
              <span className="text-[11px] font-semibold uppercase tracking-[0.14em] text-slate-500">
                GST on the price
              </span>
              <CustomSelect
                value={draft.taxMode || 'exclusive'}
                onChange={(v) => setDraft({ ...draft, taxMode: v })}
                placeholder="— Select Tax Mode —"
                options={[
                  { value: 'exclusive', label: 'Exclusive GST · tax on top' },
                  { value: 'inclusive', label: 'Inclusive GST · MRP (tax inside)' },
                ]}
                className="w-full rounded-lg bg-gray-50 border border-gray-100 normal-case text-[#0d0d0d]"
              />
            </label>
          </div>
        )}

        <label className="block space-y-1">
          <span className="text-[11px] font-semibold uppercase tracking-[0.14em] text-slate-500">
            Name <span className="text-rose-500 ml-1">*</span>
          </span>
          <input
            type="text"
            value={draft.name}
            onChange={(e) => {
              setDraft({ ...draft, name: formatCategoryName(e.target.value) });
              if (errors.name) setErrors({ ...errors, name: undefined });
            }}
            placeholder={`e.g. ${title}`}
            className={`w-full rounded-lg bg-gray-50 border ${errors.name ? 'border-rose-400 focus:border-rose-500' : 'border-gray-100 focus:border-[var(--theme-primary)]'} focus:bg-white outline-none px-3 py-2 text-xs font-medium text-[#0d0d0d]`}
          />
          {errors.name && <p className="text-[11px] font-medium text-rose-500">{errors.name}</p>}
        </label>
        <label className="block space-y-1">
          <span className="text-[11px] font-semibold uppercase tracking-[0.14em] text-slate-500">Description</span>
          <textarea
            rows={2}
            value={draft.description || ''}
            onChange={(e) => setDraft({ ...draft, description: e.target.value })}
            className="w-full rounded-lg bg-gray-50 border border-gray-100 focus:bg-white focus:border-[var(--theme-primary)] outline-none px-3 py-2 text-xs font-medium text-[#0d0d0d] resize-none"
          />
        </label>
      </div>

      <footer className="px-6 py-4 border-t border-[var(--theme-tertiary)] flex items-center justify-end gap-3 shrink-0 bg-slate-50">
        <button type="button" onClick={onClose} className="px-5 py-2.5 text-xs font-semibold uppercase tracking-[0.14em] text-slate-500 hover:text-slate-700">Cancel</button>
        <button
          type="button"
          onClick={handleSubmit}
          className="inline-flex items-center gap-2 px-5 py-2.5 rounded-lg text-white text-xs font-semibold uppercase tracking-[0.14em]"
          style={{ background: accent }}
        >
          {mode === 'edit' ? 'Save' : `Create ${title.toLowerCase()}`}
        </button>
      </footer>
    </motion.section>
  );
};

Form.propTypes = {
  open: PropTypes.bool.isRequired,
  draft: PropTypes.object.isRequired,
  setDraft: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
  mode: PropTypes.string.isRequired,
  title: PropTypes.string.isRequired,
  needsParent: PropTypes.bool,
  parents: PropTypes.array,
  parentLabel: PropTypes.string,
  gstRates: PropTypes.array,
  accent: PropTypes.string.isRequired,
};

export const MasterCategoryTab = ({
  org, title, singular, list, needsParent = false, parents = [], parentLabel = 'Parent',
  gstRates = [],
  onCreate, onUpdate, onDelete, showNotification }) => {
  const { version: dataVersion } = useSharedStore();
  const [version, setVersion] = useState(0);
  const [search, setSearch] = useState('');
  const [modalOpen, setModalOpen] = useState(false);
  const [mode, setMode] = useState('create');
  const [draft, setDraft] = useState(blank(org.id, needsParent));
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [viewMode, setViewMode] = useState('grid');

  const items = useMemo(() => {
    const q = search.trim().toLowerCase();
    const currentList = typeof list === 'function' ? list() : list;
    if (!q) return currentList;
    return currentList.filter((it) => (it.name || '').toLowerCase().includes(q));
  }, [list, search, version, dataVersion]);



  const accent = org.theme.primary;

  const openCreate = () => { setDraft(blank(org.id, needsParent)); setMode('create'); setModalOpen(true); };
  const openEdit = (rec) => { setDraft({ taxMode: 'exclusive', ...JSON.parse(JSON.stringify(rec)) }); setMode('edit'); setModalOpen(true); };

  const submit = (record) => {
    const final = { ...record, orgId: org.id };
    if (mode === 'create') onCreate(final);
    else onUpdate(record.id, final);
    if (showNotification) showNotification(`${mode === 'create' ? 'Created' : 'Updated'} ${final.name}`, 'success');
    setModalOpen(false);
    setVersion((v) => v + 1);
  };

  const doDelete = () => {
    if (!confirmDelete) return;
    onDelete(confirmDelete.id);
    if (showNotification) showNotification(`Removed ${confirmDelete.name}`, 'success');
    setConfirmDelete(null);
    setVersion((v) => v + 1);
  };

  const toggleStatus = (it) => {
    const newStatus = (it.status === 'Active' || !it.status) ? 'Inactive' : 'Active';
    const final = { ...it, status: newStatus, orgId: org.id };
    onUpdate(it.id, final);
    if (showNotification) showNotification(`${final.name} is now ${newStatus}`, 'success');
    setVersion((v) => v + 1);
  };

  if (modalOpen) {
    return (
      <div className="w-full">
        <Form
          open={modalOpen}
          draft={draft}
          setDraft={setDraft}
          onClose={() => setModalOpen(false)}
          onSubmit={submit}
          mode={mode}
          title={singular}
          needsParent={needsParent}
          parents={parents}
          parentLabel={parentLabel}
          gstRates={gstRates}
          accent={accent}
          showNotification={showNotification}
        />
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-4">
      <section className="bg-white border border-[var(--theme-tertiary)] rounded-md px-4 py-3 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between shadow-sm">
        <div className="flex flex-1 min-w-0 items-center gap-3 flex-wrap">
          <div className="relative flex-1 min-w-[180px] max-w-md">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" aria-hidden="true" />
            <input
              type="search"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder={`Search ${title.toLowerCase()}…`}
              className="w-full bg-[var(--theme-secondary)] border border-[var(--theme-tertiary)] focus:bg-white focus:border-[var(--theme-primary)] rounded-md pl-9 pr-4 py-1.5 text-xs font-medium text-[#0d0d0d] outline-none transition-colors"
            />
          </div>
          <ViewToggle value={viewMode} onChange={setViewMode} />
        </div>
        <button
          type="button"
          onClick={openCreate}
          className="inline-flex items-center gap-2 px-4 py-2 rounded-lg text-white text-xs font-semibold uppercase tracking-[0.14em] shadow-lg shrink-0"
          style={{ background: accent, boxShadow: `0 12px 24px -12px ${accent}` }}
        >
          <Plus size={14} aria-hidden="true" /> New {singular.toLowerCase()}
        </button>
      </section>

      {viewMode === 'grid' ? (
        <section className="grid grid-cols-[repeat(auto-fill,minmax(220px,1fr))] gap-4">
          {items.map((it) => {
            const parent = needsParent ? parents.find((p) => p.id === it.categoryId) : null;
            return (
              <motion.article
                key={it.id}
                layout
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                className="h-full bg-white border border-[var(--theme-tertiary)] rounded-lg overflow-hidden flex flex-col hover:border-[var(--theme-primary-tint)] transition-colors"
              >
                <div className="aspect-[4/3] w-full overflow-hidden shrink-0 relative" style={{ background: 'var(--theme-secondary)' }}>
                  {it.image
                    ? <img src={it.image} alt={it.name} className="w-full h-full object-cover" />
                    : <div className="w-full h-full flex items-center justify-center text-slate-300">{needsParent ? <Layers size={32} aria-hidden="true" /> : <FolderTree size={32} aria-hidden="true" />}</div>}
                </div>
                <div className="p-3.5 flex flex-col gap-2 flex-1 min-w-0">
                  <div className="min-w-0">
                    <div className="flex items-center justify-between gap-2">
                      {parent ? (
                        <p className="text-[11px] font-semibold uppercase tracking-[0.14em] truncate" style={{ color: accent }}>{parent.name}</p>
                      ) : <span />}
                      {needsParent && (
                        <span className="text-[10px] font-semibold uppercase tracking-wider text-slate-400 shrink-0 ml-auto">
                          {it.taxMode === 'inclusive' ? 'Inclusive' : 'Exclusive'}
                        </span>
                      )}
                    </div>
                    <p className="text-[13px] font-extrabold text-[#0d0d0d] truncate">{it.name}</p>
                    {it.description && <p className="text-[10px] font-medium text-slate-500 line-clamp-1 mt-0.5">{it.description}</p>}
                    {needsParent && gstRates.find((g) => g.id === it.gstId) && (
                      <p className="text-[10px] font-medium text-slate-400 mt-1">
                        GST: {gstRates.find((g) => g.id === it.gstId).name} ({gstRates.find((g) => g.id === it.gstId).rate}%)
                      </p>
                    )}
                  </div>
                  <footer className="flex items-center justify-end gap-2 mt-auto pt-1.5 border-t border-[var(--theme-tertiary)]">
                    <button type="button" aria-label={`Edit ${it.name}`} onClick={() => openEdit(it)} className="inline-flex items-center gap-1.5 px-2 py-1 rounded bg-transparent border border-transparent text-slate-500 hover:bg-white hover:border-slate-300 hover:text-[var(--theme-primary)] hover:shadow-sm text-[10px] font-bold uppercase tracking-[0.1em] transition-all">
                      <Pen size={12} /> Edit
                    </button>
                    <button type="button" aria-label={`Remove ${it.name}`} onClick={() => setConfirmDelete(it)} className="inline-flex items-center gap-1.5 px-2 py-1 rounded bg-transparent border border-transparent text-slate-500 hover:bg-white hover:border-slate-300 hover:text-rose-600 hover:shadow-sm text-[10px] font-bold uppercase tracking-[0.1em] transition-all">
                      <Trash2 size={12} /> Remove
                    </button>
                  </footer>
                </div>
              </motion.article>
            );
          })}
          {items.length === 0 && (
            <div className="col-span-full py-16 bg-white border border-dashed border-[var(--theme-tertiary)] rounded-lg text-center">
              <p className="text-[12px] font-bold uppercase tracking-[0.16em] text-slate-400">No {title.toLowerCase()} yet.</p>
              <button type="button" onClick={openCreate} className="mt-3 inline-flex items-center gap-2 px-4 py-2 rounded-lg text-white text-xs font-semibold uppercase tracking-[0.14em]" style={{ background: accent }}>
                <Plus size={12} /> Create the first one
              </button>
            </div>
          )}
        </section>
      ) : (
        <section className="bg-white border border-[var(--theme-tertiary)] rounded-lg overflow-hidden shadow-sm">
          <div className="overflow-x-auto scrollbar-hide">
            <table className="w-full text-left">
              <thead className="text-[10px] font-semibold uppercase tracking-[0.14em] text-slate-400 border-b border-slate-200 bg-slate-50">
                <tr>
                  <th className="px-4 py-3 w-16 whitespace-nowrap">{title} Image</th>
                  <th className="px-4 py-3 whitespace-nowrap">{title} Name</th>
                  {needsParent && <th className="px-4 py-3 whitespace-nowrap">{parentLabel}</th>}
                  {needsParent && <th className="px-4 py-3 whitespace-nowrap">GST Rate</th>}
                  {needsParent && <th className="px-4 py-3 whitespace-nowrap">Tax Mode</th>}
                  <th className="px-4 py-3 text-center whitespace-nowrap">Status</th>
                  <th className="px-4 py-3 text-right whitespace-nowrap">Actions</th>
                </tr>
              </thead>
              <tbody>
                {items.map((it) => {
                  const parent = needsParent ? parents.find((p) => p.id === it.categoryId) : null;
                  const gstObj = gstRates.find((g) => g.id === it.gstId);
                  return (
                    <motion.tr
                      key={it.id}
                      layout
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      className="border-b border-[var(--theme-tertiary)] last:border-0 transition-colors hover:bg-slate-50 group"
                    >
                      <td className="px-4 py-2">
                        <div className="w-10 h-10 rounded-md overflow-hidden bg-[var(--theme-secondary)] flex items-center justify-center shrink-0 border border-slate-100">
                          {it.image ? (
                            <img src={it.image} alt={it.name} className="w-full h-full object-cover" />
                          ) : (
                            needsParent ? <Layers size={16} className="text-slate-300" aria-hidden="true" /> : <FolderTree size={16} className="text-slate-300" aria-hidden="true" />
                          )}
                        </div>
                      </td>
                      <td className="px-4 py-3 text-[13px] font-extrabold text-[#0d0d0d] whitespace-nowrap">{it.name}</td>
                      {needsParent && (
                        <td className="px-4 py-3 text-[12px] font-bold whitespace-nowrap" style={{ color: accent }}>{parent ? parent.name : '—'}</td>
                      )}
                      {needsParent && (
                        <td className="px-4 py-3 text-[12px] font-medium text-slate-700 whitespace-nowrap">
                          {gstObj ? `${gstObj.name} (${gstObj.rate}%)` : '—'}
                        </td>
                      )}
                      {needsParent && (
                        <td className="px-4 py-3 text-[12px] font-medium text-slate-700 whitespace-nowrap capitalize">
                          {it.taxMode === 'inclusive' ? 'Inclusive' : 'Exclusive'}
                        </td>
                      )}
                      <td className="px-4 py-3 text-center whitespace-nowrap">
                        <div className="flex justify-center">
                          <StatusToggle
                            on={it.status === 'Active' || !it.status}
                            label={it.name}
                            onChange={() => toggleStatus(it)}
                          />
                        </div>
                      </td>
                      <td className="px-4 py-3 text-right whitespace-nowrap">
                        <RowActions>
                          <IconAction icon={Pen} label={`Edit ${it.name}`} title="Edit" onClick={() => openEdit(it)} />
                          <IconAction icon={Trash2} tone="danger" label={`Remove ${it.name}`} title="Remove" onClick={() => setConfirmDelete(it)} />
                        </RowActions>
                      </td>
                    </motion.tr>
                  );
                })}
                {items.length === 0 && (
                  <tr>
                    <td colSpan={needsParent ? 7 : 4} className="px-5 py-16 text-center">
                      <p className="text-[12px] font-bold uppercase tracking-[0.16em] text-slate-400">No {title.toLowerCase()} yet.</p>
                      <button type="button" onClick={openCreate} className="mt-3 inline-flex items-center gap-2 px-4 py-2 rounded-lg text-white text-xs font-semibold uppercase tracking-[0.14em]" style={{ background: accent }}>
                        <Plus size={12} /> Create the first one
                      </button>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </section>
      )}

      {confirmDelete && (
        <div className="fixed inset-0 z-[130] flex items-center justify-center p-4" style={{ background: 'rgba(15,23,42,0.6)', backdropFilter: 'blur(6px)' }}>
          <div className="w-full max-w-sm bg-white rounded-lg shadow-xl border border-rose-100 overflow-hidden">
            <div className="px-6 py-6 text-center">
              <div className="w-14 h-14 mx-auto rounded-full flex items-center justify-center text-rose-500 mb-3" style={{ background: 'rgba(244,63,94,0.10)' }}><Trash2 size={20} /></div>
              <h4 className="text-base font-extrabold text-[#0d0d0d]">Remove {confirmDelete.name}?</h4>
              <p className="mt-1 text-[11px] font-medium text-slate-500">{needsParent ? 'This entry will be removed from the catalog.' : 'Sub-categories under this entry will also be removed.'}</p>
            </div>
            <div className="flex border-t border-rose-100">
              <button type="button" onClick={() => setConfirmDelete(null)} className="flex-1 py-3 text-xs font-semibold uppercase tracking-[0.14em] text-slate-500 hover:bg-slate-50">Cancel</button>
              <button type="button" onClick={doDelete} className="flex-1 py-3 text-xs font-semibold uppercase tracking-[0.14em] bg-rose-500 text-white hover:bg-rose-600">Remove</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

MasterCategoryTab.propTypes = {
  org: PropTypes.object.isRequired,
  title: PropTypes.string.isRequired,
  singular: PropTypes.string.isRequired,
  list: PropTypes.oneOfType([PropTypes.array, PropTypes.func]).isRequired,
  needsParent: PropTypes.bool,
  parents: PropTypes.array,
  parentLabel: PropTypes.string,
  gstRates: PropTypes.array,
  onCreate: PropTypes.func.isRequired,
  onUpdate: PropTypes.func.isRequired,
  onDelete: PropTypes.func.isRequired,
  showNotification: PropTypes.func,
};
